// A function to check whether the file size is larger than the limit or not
export function checkFileSize(file, maxSizeMb) {
    return file.size / 1024 / 1024 > maxSizeMb;
}

// A function to convert blob to base64
export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// A function to convert base64 to blob
export function base64toBlob(base64, mimeType = "image/webp", hasPrefix = true) {
    if (hasPrefix) base64 = base64.replace(/^data:image\/[a-z]+;base64,/, "");

    const binStr = window.atob(base64);
    const arr = new Array(binStr.length);
    for (let i = 0; i < binStr.length; i++) {
        arr[i] = binStr.charCodeAt(i);
    }

    return new Blob([arr], { mimeType });
}

// A function to convert blob to file
export function blobToFile(blob, fileName, mimeType, lastModified = new Date().getTime()) {
    if (!blob) return null;

    if (!mimeType) mimeType = blob.type;

    return new File([blob], fileName, { lastModified, type: mimeType });
}

export function getEndpointPathWithoutOrigin(fullUrl) {
    const parts = fullUrl.split("/");
    parts.splice(0, 3);
    return parts.join("/");
}

/**
 * To check whether the param obj is an object
 * @param {Object} obj Object 
 * @returns {Boolean} Whether the param obj is an object
 */
export function checkIsObject(obj) {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

/**
 * To map the failed file usage type to the corresponding form fields
 * @param {Array} failedFiles Failed file list of upload
 * @param {Function | Object} getText Function to get text from usage type or object mapping
 * @returns {Array<string>} Array of failed upload item with specific form fields
 */
export function getFailedUploadItemTextFromFailedList(failedFiles, getText) {
    if (!failedFiles || !failedFiles.length) return [];

    let getTextCallback = null;

    const isGetTextObject = checkIsObject(getText);
    const isGetTextFunc = typeof getText === "function";
    if (isGetTextFunc) {
        getTextCallback = getText;
    } else if (isGetTextObject) {
        getTextCallback = generateGetValueFromMappingFunc(getText);
    } else {
        throw new Error("Invalid param: getText");
    }

    return failedFiles
        .map((el) => getTextCallback(el.usageType))
        .filter((d) => !!d)
        .reduce((acc, curr) => {
            return acc.includes(curr) ? acc : [...acc, curr];
        }, []);
}

/**
 * Generate a function to get value from mapping
 * @param {Object} mapping Key Value mapping
 * @returns {Function} Function to get value from mapping
 */
export function generateGetValueFromMappingFunc(mapping) {
    return function (key = "") {
        if (!key) return "";

        return mapping[key] || "";
    }
}

/*
type UpdateOSSUrlToDbRequestCommand = {
    command: "INSERT" | "UPDATE" | "DELETE";
    originalId: string | number;
    originalUrl: string;
    originalOrderId: number;
    usageType: string;
    newId: string;
    newUrl: string;
    newOrderId: number;
}

type UpdateOSSUrlToDbRequestCdnInfo = {
    id: string;
    usageType: string;
    uploadedOssUrl: string;
}

type UpdateOSSUrlToDbRequest = {
    Commands: UpdateOSSUrlToDbRequestCommand[];
    UploadedList: UpdateOSSUrlToDbRequestCdnInfo[];
    FailedList: UpdateOSSUrlToDbRequestCdnInfo[];
}

type GetUpdateOSSUrlToDbRequestProps = {
    commands: UpdateOSSUrlToDbRequestCommand[];
    successfulList: UpdateOSSUrlToDbRequestCdnInfo[];
    failedList: UpdateOSSUrlToDbRequestCdnInfo[];
}
*/

/**
 * To map the request data
 * @param {GetUpdateOSSUrlToDbRequestProps} uploadedResult Uploaded Result Data
 * @returns {UpdateOSSUrlToDbRequest} Formatted data for request
 */
export function getUpdateOSSUrlToDbRequest(uploadedResult) {
    const commands = uploadedResult.commands.map((i) => ({
        command: i.command,
        originalId: i.originalId?.toString(),
        originalUrl: i.originalUrl,
        originalOrderId: i.originalOrderId,
        usageType: i.usageType,
        newId: i.newId,
        newUrl: i.newUrl,
        newOrderId: i.newOrderId,
    }));

    const uploadedList = uploadedResult.successfulList.map((i) => ({
        id: i.id,
        usageType: i.usageType,
        uploadedOssUrl: i.uploadedOssUrl,
    }));

    const failedList = uploadedResult.failedList.map((i) => ({
        id: i.id,
        usageType: i.usageType,
        uploadedOssUrl: "",
    }));

    return {
        Commands: commands,
        UploadedList: uploadedList,
        FailedList: failedList,
    };
}

export function getDeleteOSSUrlOnDbCommand(originalId, originalUrl, originalOrderId, usageType) {
    return {
        command: "DELETE",
        usageType,
        originalId,
        originalUrl,
        originalOrderId,
        newId: "",
        newUrl: "",
        newOrderId: 0,
    };
}
